/**
 * Geohash encode/decode/neighbours.
 *
 * Chosen over H3/PostGIS because prefix-containment lets cell lookup be a plain
 * B-tree `WHERE gh5 IN (...)` on D1/SQLite with no extension, and the same ~100
 * lines run unchanged in the Worker and in the browser.
 *
 * gh5 ~= 4.9 x 4.9 km  -> the alert bundle unit (one R2 object per live cell)
 * gh6 ~= 1.2 x 0.6 km  -> the dedup unit
 */
const BASE32 = "0123456789bcdefghjkmnpqrstuvwxyz";

const NEIGHBOUR = {
  n: ["p0r21436x8zb9dcf5h7kjnmqesgutwvy", "bc01fg45238967deuvhjyznpkmstqrwx"],
  s: ["14365h7k9dcfesgujnmqp0r2twvyx8zb", "238967debc01fg45kmstqrwxuvhjyznp"],
  e: ["bc01fg45238967deuvhjyznpkmstqrwx", "p0r21436x8zb9dcf5h7kjnmqesgutwvy"],
  w: ["238967debc01fg45kmstqrwxuvhjyznp", "14365h7k9dcfesgujnmqp0r2twvyx8zb"],
} as const;

const BORDER = {
  n: ["prxz", "bcfguvyz"],
  s: ["028b", "0145hjnp"],
  e: ["bcfguvyz", "prxz"],
  w: ["0145hjnp", "028b"],
} as const;

export type Bounds = { minLat: number; maxLat: number; minLon: number; maxLon: number };

export function encodeGeohash(lat: number, lon: number, precision = 5): string {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) throw new Error("geohash: non-finite coordinate");
  let idx = 0;
  let bit = 0;
  let evenBit = true;
  let hash = "";
  let latMin = -90;
  let latMax = 90;
  let lonMin = -180;
  let lonMax = 180;

  while (hash.length < precision) {
    if (evenBit) {
      const lonMid = (lonMin + lonMax) / 2;
      if (lon >= lonMid) {
        idx = idx * 2 + 1;
        lonMin = lonMid;
      } else {
        idx = idx * 2;
        lonMax = lonMid;
      }
    } else {
      const latMid = (latMin + latMax) / 2;
      if (lat >= latMid) {
        idx = idx * 2 + 1;
        latMin = latMid;
      } else {
        idx = idx * 2;
        latMax = latMid;
      }
    }
    evenBit = !evenBit;
    if (++bit === 5) {
      hash += BASE32.charAt(idx);
      bit = 0;
      idx = 0;
    }
  }
  return hash;
}

export function geohashBounds(hash: string): Bounds {
  if (hash.length === 0) throw new Error("geohash: empty hash");
  let evenBit = true;
  let latMin = -90;
  let latMax = 90;
  let lonMin = -180;
  let lonMax = 180;

  for (const ch of hash.toLowerCase()) {
    const idx = BASE32.indexOf(ch);
    if (idx === -1) throw new Error(`geohash: invalid character '${ch}'`);
    for (let n = 4; n >= 0; n--) {
      const bitN = (idx >> n) & 1;
      if (evenBit) {
        const lonMid = (lonMin + lonMax) / 2;
        if (bitN === 1) lonMin = lonMid;
        else lonMax = lonMid;
      } else {
        const latMid = (latMin + latMax) / 2;
        if (bitN === 1) latMin = latMid;
        else latMax = latMid;
      }
      evenBit = !evenBit;
    }
  }
  return { minLat: latMin, maxLat: latMax, minLon: lonMin, maxLon: lonMax };
}

export function decodeGeohash(hash: string): { lat: number; lon: number } {
  const b = geohashBounds(hash);
  return { lat: (b.minLat + b.maxLat) / 2, lon: (b.minLon + b.maxLon) / 2 };
}

export function geohashAdjacent(hash: string, direction: "n" | "s" | "e" | "w"): string {
  const h = hash.toLowerCase();
  const lastCh = h.charAt(h.length - 1);
  let parent = h.slice(0, -1);
  const type: 0 | 1 = (h.length % 2) as 0 | 1; // 0 = even length, 1 = odd length

  if (BORDER[direction][type].indexOf(lastCh) !== -1 && parent !== "") {
    parent = geohashAdjacent(parent, direction);
  }
  return parent + BASE32.charAt(NEIGHBOUR[direction][type].indexOf(lastCh));
}

/** The cell plus its 8 neighbours — the client's subscription window (~15x15 km at gh5). */
export function geohashNeighbours(hash: string): string[] {
  const n = geohashAdjacent(hash, "n");
  const s = geohashAdjacent(hash, "s");
  return [
    hash,
    n,
    s,
    geohashAdjacent(hash, "e"),
    geohashAdjacent(hash, "w"),
    geohashAdjacent(n, "e"),
    geohashAdjacent(n, "w"),
    geohashAdjacent(s, "e"),
    geohashAdjacent(s, "w"),
  ];
}
